import chroma from "chroma-js";
import moment from "moment";
import { ReactNode, useId, useState } from "react";
import { IoChevronDown } from "react-icons/io5";
import cn from "../../../../../../utils/classNames";

const DEFAULT_FALLBACK = "N/A";

const isEmptyValue = (value: unknown) => {
    if (value === null || value === undefined) return true;
    if (typeof value === "string" && value.trim() === "") return true;
    return false;
};

export const formatInfoValue = (value: unknown, fallback = DEFAULT_FALLBACK): string => {
    if (isEmptyValue(value)) return fallback;
    if (typeof value === "boolean") return value ? "Sí" : "No";
    return String(value);
};

export const formatInfoDate = (value: unknown, fallback = DEFAULT_FALLBACK): string => {
    if (isEmptyValue(value)) return fallback;

    let rawDate = value;
    if (typeof value === "object" && value !== null && "date" in (value as Record<string, unknown>)) {
        rawDate = (value as { date?: string }).date;
    }

    if (typeof rawDate !== "string" || rawDate.trim() === "") return fallback;

    const parsedDate = moment(rawDate.split(" ")[0]);
    return parsedDate.isValid() ? parsedDate.format("DD/MM/YYYY") : fallback;
};

interface InfoCardProps {
    title: string;
    subtitle?: ReactNode;
    action?: ReactNode;
    children: ReactNode;
    className?: string;
    collapsible?: boolean;
    defaultExpanded?: boolean;
}

export const InfoCard = ({
    title,
    subtitle,
    action,
    children,
    className,
    collapsible = true,
    defaultExpanded = true,
}: InfoCardProps) => {
    const [expanded, setExpanded] = useState(defaultExpanded);
    const contentId = useId();

    return (
        <section className={cn("overflow-hidden rounded-md border-2 border-gray-200 bg-white", className)}>
            <header className="flex flex-col gap-2 px-5 py-4 md:flex-row md:items-start md:justify-between">
                <div className="min-w-0">
                    <h3 className="text-lg font-bold text-gray-900">{title}</h3>
                    {subtitle && (
                        <p className="mt-1 text-sm leading-5 text-gray-500">{subtitle}</p>
                    )}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                    {action && (
                        <div>{action}</div>
                    )}

                    {collapsible && (
                        <button
                            type="button"
                            className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 hover:text-gray-900"
                            onClick={() => setExpanded((prev) => !prev)}
                            aria-expanded={expanded}
                            aria-controls={contentId}
                            title={expanded ? "Plegar sección" : "Desplegar sección"}
                        >
                            <span>{expanded ? "Plegar" : "Desplegar"}</span>
                            <IoChevronDown className={cn("h-4 w-4 transition-transform duration-200", expanded ? "rotate-180" : "rotate-0")} aria-hidden />
                            <span className="sr-only">{expanded ? "Plegar sección" : "Desplegar sección"}</span>
                        </button>
                    )}
                </div>
            </header>

            {(!collapsible || expanded) && (
                <div id={contentId} className="space-y-4 border-t-2 border-dashed border-gray-200 px-5 py-4">
                    {children}
                </div>
            )}
        </section>
    );
};

interface InfoGroupProps {
    title: string;
    description?: ReactNode;
    children: ReactNode;
    className?: string;
}

export const InfoGroup = ({ title, description, children, className }: InfoGroupProps) => {
    return (
        <div className={cn("overflow-hidden rounded-md border border-gray-200 bg-white", className)}>
            <div className="border-b border-dashed border-gray-200 bg-gray-50/70 px-3 py-2">
                <h4 className="text-sm font-semibold text-gray-800">{title}</h4>
                {description && (
                    <p className="mt-0.5 text-xs leading-4 text-gray-500">{description}</p>
                )}
            </div>
            <div className="grid grid-cols-12 items-start gap-2 p-2 md:p-3">
                {children}
            </div>
        </div>
    );
};

interface InfoFieldProps {
    label: string;
    value: ReactNode;
    className?: string;
}

export const InfoField = ({ label, value, className }: InfoFieldProps) => {
    const resolvedValue = isEmptyValue(value) ? DEFAULT_FALLBACK : value;

    return (
        <div className={cn("block min-w-0 col-span-12 md:col-span-6", className)}>
            <p className="rizzui-input-label mb-1.5 block text-sm font-medium text-gray-700">{label}</p>
            <div className="min-h-[42px] rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 break-words whitespace-pre-wrap">
                {resolvedValue}
            </div>
        </div>
    );
};

interface InfoStatusValue {
    name?: string | null;
    color?: string | null;
}

interface InfoStatusFieldProps {
    label: string;
    status?: InfoStatusValue | null;
    className?: string;
    fallback?: string;
}

const getStatusFieldStyles = (statusColor?: string | null) => {
    if (!statusColor || !chroma.valid(statusColor)) {
        return null;
    }

    const accentColor = chroma(statusColor).hex();
    const textColor = chroma.contrast(accentColor, "white") > 4.5
        ? accentColor
        : chroma(accentColor).darken(1.8).hex();

    return {
        accentColor,
        textColor,
        containerStyle: {
            backgroundColor: chroma(accentColor).alpha(0.08).css(),
            borderColor: chroma(accentColor).alpha(0.3).css(),
            boxShadow: `inset 0 1px 0 ${chroma(accentColor).alpha(0.08).css()}`,
        },
        pillStyle: {
            backgroundColor: chroma(accentColor).alpha(0.14).css(),
            color: textColor,
        },
    };
};

export const InfoStatusField = ({ label, status, className, fallback = DEFAULT_FALLBACK }: InfoStatusFieldProps) => {
    const hasStatus = !isEmptyValue(status?.name);
    const statusStyles = hasStatus ? getStatusFieldStyles(status?.color) : null;

    return (
        <div className={cn("block min-w-0 col-span-12 md:col-span-6", className)}>
            <p className="rizzui-input-label mb-1.5 block text-sm font-medium text-gray-700">{label}</p>
            <div
                className={cn(
                    "min-h-[42px] rounded-md border px-3 py-2 text-sm break-words transition-colors",
                    hasStatus ? "bg-white/80" : "bg-white text-gray-900",
                    !statusStyles && hasStatus ? "border-gray-200 text-gray-900" : ""
                )}
                style={statusStyles?.containerStyle}
            >
                {hasStatus ? (
                    <div className="flex min-h-[24px] items-center">
                        <span
                            className="inline-flex max-w-full items-center gap-2 rounded-full px-2.5 py-1 text-xs font-semibold shadow-sm"
                            style={statusStyles?.pillStyle}
                        >
                            <span
                                className="h-2.5 w-2.5 shrink-0 rounded-full ring-2 ring-white/80"
                                style={{ backgroundColor: statusStyles?.accentColor ?? "#9ca3af" }}
                            />
                            <span className="truncate">{status?.name}</span>
                        </span>
                    </div>
                ) : (
                    <span className="text-sm font-medium text-gray-900">{fallback}</span>
                )}
            </div>
        </div>
    );
};

interface InfoTextFieldProps {
    label: string;
    value: unknown;
    className?: string;
}

export const InfoTextField = ({ label, value, className }: InfoTextFieldProps) => {
    const resolvedValue = formatInfoValue(value);

    return (
        <div className={cn("block min-w-0 col-span-12", className)}>
            <p className="rizzui-input-label mb-1.5 block text-sm font-medium text-gray-700">{label}</p>
            <div className="min-h-[42px] rounded-md border border-gray-200 bg-white px-3 py-2 text-sm leading-5 text-gray-900 whitespace-pre-wrap">
                {resolvedValue}
            </div>
        </div>
    );
};

interface InfoBooleanFieldProps {
    label: string;
    value: boolean | null | undefined;
    className?: string;
}

export const InfoBooleanField = ({ label, value, className }: InfoBooleanFieldProps) => {
    const isKnown = value === true || value === false;

    return (
        <div className={cn("block min-w-0 col-span-12 md:col-span-6", className)}>
            <p className="rizzui-input-label mb-1.5 block text-sm font-medium text-gray-700">{label}</p>
            <div className="min-h-[42px] rounded-md border border-gray-200 bg-white px-3 py-2">
                {isKnown ? (
                    <span
                        className={cn(
                            "inline-flex rounded-full px-2.5 py-1 text-xs font-semibold",
                            value
                                ? "bg-emerald-100 text-emerald-700"
                                : "bg-rose-100 text-rose-700"
                        )}
                    >
                        {value ? "Sí" : "No"}
                    </span>
                ) : (
                    <span className="text-sm font-medium text-gray-900">{DEFAULT_FALLBACK}</span>
                )}
            </div>
        </div>
    );
};

interface InfoTagListProps {
    label: string;
    items: string[];
    className?: string;
}

export const InfoTagList = ({ label, items, className }: InfoTagListProps) => {
    return (
        <div className={cn("block min-w-0 col-span-12", className)}>
            <p className="rizzui-input-label mb-1.5 block text-sm font-medium text-gray-700">{label}</p>
            <div className="min-h-[42px] rounded-md border border-gray-200 bg-white px-3 py-2">
                <div className="mt-0.5 flex flex-wrap gap-1.5">
                    {items.length > 0 ? items.map((item, index) => (
                        <span
                            key={`${item}-${index}`}
                            className="rounded-full border border-gray-200 bg-gray-50 px-2.5 py-0.5 text-xs font-medium text-gray-700"
                        >
                            {item}
                        </span>
                    )) : (
                        <span className="text-sm font-medium text-gray-900">{DEFAULT_FALLBACK}</span>
                    )}
                </div>
            </div>
        </div>
    );
};
